import { cx } from '@linaria/core';
import { useId, useRef, useState, useEffect } from 'react';
import { Dropdown, Listbox } from '@ui/kit';
import { colors } from '@ui/kit/design-tokens';
import { SentenceElementBiled, SentenceElementHensynsled, SentenceElementObjekt, SentenceElementVerballed, SentenceElementSubjekt } from '@ui/kit/icons';
import { wordDropdownInput } from '@utils/component-framework';
import { useToggleState, Chevron } from '@utils/react';
import { useOpgavekompleksetEntry } from '../../../../state/hook.entry.js';
import { useUpdateInputMutation } from '../../../../state/mutations/update-input.js';
import { useInputState } from '../../hook.use-input-state.js';
import { StateBox } from '../atom.state-box/_state-box.js';
import { stateBox, selectedValue, button, label, chevron, dropdown } from './input.word-dropdown.style.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';

function WordDropdownInput(props) {
  const triggerId = useId();
  const listboxId = useId();
  const buttonRef = useRef(null);
  const [showDropdown, dropdownActions] = useToggleState(false);
  const isLastWordOnLine = useIsLastWordOnLine(buttonRef);
  const update = useUpdateInputMutation();
  const inputEntry = useOpgavekompleksetEntry({
    select: entry => wordDropdownInput.entry.asType(entry?.data.tasks[props.taskEntryKey]?.details[props.node.id])
  });
  const onChange = index => {
    dropdownActions.disable();
    update(props.taskEntryKey, props.node.id, {
      type: "word-dropdown",
      selectedIndex: inputEntry?.selectedIndex === index ? null : index
    });
  };
  const state = useInputState(props.node, inputEntry);
  const selectedValue$1 = typeof inputEntry?.selectedIndex === "number" ? props.node.options[inputEntry?.selectedIndex] : undefined;
  if (state === "readonly") {
    return /*#__PURE__*/jsx(ReadonlyDropdown, {
      label: props.node.label,
      selectedValue: selectedValue$1?.label
    });
  }
  if (state === "correct" || state === "incorrect") {
    const shouldHaveValue = wordDropdownInput.getCorrectOption(props.node)?.label;
    const boxState = state === "incorrect" && props.facitType === "should-have" ? "should-have" : state;
    const value = boxState === "should-have" ? shouldHaveValue : selectedValue$1?.label;
    if (boxState === "should-have" && !value) {
      return /*#__PURE__*/jsx(ReadonlyDropdown, {
        label: props.node.label
      });
    }
    return /*#__PURE__*/jsxs(StateBox, {
      state: boxState,
      textTheme: "colored",
      className: stateBox,
      children: [props.node.label, value && /*#__PURE__*/jsx("span", {
        className: cx(selectedValue, !showDropdown && "visible"),
        "data-wide": value.length >= 5,
        children: transformLabelToNode(value)
      })]
    });
  }
  const options = props.node.options.map(option => ({
    ...option,
    label: transformLabelToNode(option.label)
  }));
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsxs("button", {
      ref: buttonRef,
      onClick: dropdownActions.toggle,
      className: button,
      "aria-haspopup": "listbox",
      id: triggerId,
      "aria-expanded": showDropdown,
      "aria-controls": listboxId,
      "data-last-word-on-line": isLastWordOnLine,
      children: [/*#__PURE__*/jsxs("span", {
        className: label,
        children: [props.node.label, selectedValue$1 && /*#__PURE__*/jsx("span", {
          className: cx(selectedValue, !showDropdown && "visible"),
          "data-wide": selectedValue$1.label.length >= 5,
          children: transformLabelToNode(selectedValue$1.label)
        })]
      }), /*#__PURE__*/jsx(Chevron, {
        className: chevron,
        vertical: true,
        inversed: showDropdown,
        size: 6,
        lineWidth: 1.5
      })]
    }), /*#__PURE__*/jsx(Dropdown, {
      active: showDropdown,
      triggerRef: buttonRef,
      onClose: dropdownActions.disable,
      placement: "bottom",
      restoreFocus: true,
      offsetPx: 8,
      children: /*#__PURE__*/jsx(Listbox, {
        id: listboxId,
        className: dropdown,
        selectedValues: typeof inputEntry?.selectedIndex === "number" ? [inputEntry.selectedIndex] : undefined,
        labelledBy: triggerId,
        toggleValue: onChange,
        options: options,
        disableIcons: true
      })
    })]
  });
}
function ReadonlyDropdown(props) {
  return /*#__PURE__*/jsx("span", {
    className: button,
    children: /*#__PURE__*/jsxs("span", {
      className: label,
      children: [props.label, props.selectedValue && /*#__PURE__*/jsx("span", {
        className: cx(selectedValue),
        "data-wide": props.selectedValue.length >= 5,
        children: transformLabelToNode(props.selectedValue)
      })]
    })
  });
}
function useIsLastWordOnLine(ref) {
  const [isLastWordOnLine, setIsLastWord] = useState(false);
  useEffect(() => {
    const target = ref.current;
    const nextWord = (() => {
      const base = target?.nextElementSibling;
      if (/^[\s.,?!]/.test(base?.textContent ?? "")) {
        return base?.nextElementSibling;
      }
      return base;
    })();
    if (!target) {
      return;
    }
    if (!nextWord) {
      setIsLastWord(true);
      return;
    }
    if (!(nextWord instanceof HTMLElement)) {
      return;
    }
    const onUpdate = () => {
      setIsLastWord(nextWord.offsetTop > target.offsetTop + target.clientHeight);
    };
    onUpdate();
    window.addEventListener("resize", onUpdate);
    window.addEventListener("orientationchange", onUpdate);
    return () => {
      window.removeEventListener("resize", onUpdate);
      window.removeEventListener("orientationchange", onUpdate);
    };
  }, [ref]);
  return isLastWordOnLine;
}

/**
 * SentenceElements have historically used poor unicode-symbols in the editors,
 * however we would like to represent these as nicer SVGs in the frontend.
 *
 * See ADO #126435
 */
function transformLabelToNode(label) {
  switch (label) {
    case "X":
      return /*#__PURE__*/jsx(SentenceElementSubjekt, {
        style: {
          marginTop: 2,
          color: colors.black()
        }
      });
    case "O":
      return /*#__PURE__*/jsx(SentenceElementVerballed, {
        style: {
          marginTop: 2,
          color: colors.black()
        }
      });
    case "Δ":
      return /*#__PURE__*/jsx(SentenceElementObjekt, {
        style: {
          marginTop: 2,
          color: colors.black()
        }
      });
    case "⬜":
      return /*#__PURE__*/jsx(SentenceElementHensynsled, {
        style: {
          marginTop: 2,
          color: colors.black()
        }
      });
    case "~":
      return /*#__PURE__*/jsx(SentenceElementBiled, {
        style: {
          marginTop: 2,
          color: colors.black()
        }
      });
    default:
      return label;
  }
}

export { WordDropdownInput };
//# sourceMappingURL=input.word-dropdown.js.map
